export { default as BigNumber } from '@libs/bigNumber/bignumber';

export function computeRangePositionFromK(
	k : number,
	rangeMin : number,
	rangeMax : number
)
{
	const rangeSize = Math.abs(rangeMin - rangeMax);
	const min = Math.min(rangeMin, rangeMax);
	return (min + (rangeSize * k));
};

export function computeRangePositionFromPercent(
	percent : number,
	rangeMin : number,
	rangeMax : number
)
{
	return (computeRangePositionFromK(percent / 100, rangeMin, rangeMax));
};

export function computeRangePercentFromPosition(
	position : number,
	rangeMin : number,
	rangeMax : number
)
{
	const d = 0 - rangeMin;
	const positionFromRange = position + d;
	const length = rangeMax - rangeMin;
	return ((1 / (length / positionFromRange)) * 100)
};

export function floatsEq(
	lhs : number,
	rhs : number,
	accuracity : number
)
{
	return (Math.abs(lhs - rhs) < accuracity);
};

export function getClosestUpperPow2(size : number) {
	return (Math.pow(2, Math.ceil(Math.log(size) / Math.log(2))));
};

export function degToRad(deg : number) {
	return (deg * (Math.PI / 180));
};

export function degNormalize(deg : number) {
	while (deg < 0) {
		deg = 360 + deg;
	}
	deg = deg % 360;
	return (deg);
};
export const maxAccuracity = 11;
export const maxAccuracityMultiplier = Math.pow(10, maxAccuracity);

//!\ Do not take into account decimal digit after maxAccuracity
export function round(
	n : number,
	accuracity : number
)
{
	n = Math.round(n * maxAccuracityMultiplier) / maxAccuracityMultiplier;
	const rounded = +(Math.round(<any> (n + 'e+' + accuracity)) + 'e-' + accuracity);
	return (rounded);
};

//!\ Do not take into account decimal digit after maxAccuracity
export function mod(
	lhs : number,
	rhs : number,
	accuracity : number
)
{
	if (Number.isInteger(rhs)) {
		return (lhs % rhs);
	}
	rhs = Math.abs(rhs);
	const multiplier = 1 / rhs;
	const lhsMultiplied = Math.round((lhs * multiplier) * maxAccuracityMultiplier) / maxAccuracityMultiplier;
	const rhsMultiplied = Math.round((rhs * multiplier) * maxAccuracityMultiplier) / maxAccuracityMultiplier;
	return (round(
		(lhsMultiplied % rhsMultiplied) / multiplier,
		accuracity
	));
};

export function gcd(
	lhs : number,
	rhs : number
)
{
	if (Number.isNaN(lhs) || Number.isNaN(rhs)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $lhs or $rhs parameter could not be NaN!', {
			lhs, rhs
		});
	}
	let r : number;
	while (rhs !== 0) {
		r = lhs % rhs;
		lhs = rhs;
		rhs = r;
	}
	return (lhs);
};

export function getTenPowerNeededToHaveIntegerPart(n : number) {
	if (Number.isNaN(n)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $n could not be NaN!', {
			n
		});
	}
	let power = 0;
	while (n < 1) {
		n *= 10;
		power++;
	}
	return (power);
};

export function getTenPowerNeededToHaveFullIntegerPart(n : number) {
	if (Number.isNaN(n)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $n could not be NaN!', {
			n
		});
	}
	let power = getTenPowerNeededToHaveIntegerPart(n);
	n = n * Math.pow(10, power);
	while (n !== Math.trunc(n)) {
		n *= 10;
		power++;
	}
	return (power);
};


export function closest(
	n : number,
	possibilities : number[]
)
{
	return possibilities.map<[ number, number ]>(x => [ x, Math.abs(n - x) ]).reduce(
		(a, c) => {
			return Math.min(a[ 1 ], c[ 1 ]) === a[ 1 ] ? a : c;
		},
		[ Infinity, Infinity ]
	)[0];
};

export function floorToStep(
	n : number,
	step : number
)
{
	if (Number.isNaN(n) || Number.isNaN(step)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $n or $step parameter could not be NaN!', {
			n, step
		});
	}
	const power = getTenPowerNeededToHaveFullIntegerPart(step);
	return (round(n - mod(n, step, power), power));
};

export function ceilToStep(
	n : number,
	step : number
)
{
	if (Number.isNaN(n) || Number.isNaN(step)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $n or $step parameter could not be NaN!', {
			n, step
		});
	}
	const n2 = floorToStep(n, step);
	const d = n2 - n;
	if (n < 0) {
		return (
			(d > 0) ? (n2 + -step) :
				n2
		);
	} else {
		return (
			(d < 0) ? (n2 + step) :
				n2
		);
	}
};

export function roundToStep(
	n : number,
	step : number
)
{
	if (Number.isNaN(n) || Number.isNaN(step)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $n or $step parameter could not be NaN!', {
			n, step
		});
	}
	const nMin = floorToStep(n, step);
	const nMax = ceilToStep(n, step);
	return closest(n, [ nMin, nMax ]);
};

// export function roundToStep( // todo
export function computeSizeAfterRotation(
	initialWidth : number,
	initialDepth : number,
	rotationDegree : number
)
{
	const x = initialWidth;
	const z = initialDepth;
	const angleRad = degToRad(rotationDegree);
	return ({
		x : Math.abs(x * Math.sin(angleRad)) + Math.abs(z * Math.cos(angleRad)),
		z : Math.abs(x * Math.cos(angleRad)) + Math.abs(z * Math.sin(angleRad))
	});
};

export function rotatePointOnCircle(
	circleOrigin : [ number, number ],
	point : [ number, number ],
	angle : number
)
{
	const newPoint = {
		x : circleOrigin[ 0 ] +
					(point[ 0 ] - circleOrigin[ 0 ]) *
					Math.cos(angle) -
					(point[ 1 ] - circleOrigin[ 1 ]) *
					Math.sin(angle),
		y : circleOrigin[ 1 ] +
					(point[ 0 ] - circleOrigin[ 0 ]) *
					Math.sin(angle) +
					(point[ 1 ] - circleOrigin[ 1 ]) *
					Math.cos(angle)
	};
	return (newPoint);
}

import * as E from './errors';
